// app_client/src/pages/admin/AccountDetail.jsx
import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Plus, Loader2, Trash2, AlertTriangle, CheckCircle2, Notebook } from 'lucide-react';
import { toast } from 'react-hot-toast';

// Store'lar
import useAccountStore from '../../store/useAccountStore';
import useTransactionStore from '../../store/useTransactionStore';
import useAuthStore from '../../store/authStore';

// Yardımcılar & Bileşenler
import AccountForm from '../../components/accounts/AccountForm';
import AccountInvoicesTab from '../../components/accounts/AccountInvoicesTab';
import AccountTransactions from '../../components/accounts/AccountTransactions';
import TransactionModal from '../../components/modals/TransactionModal';
import AccountPortfolioCard from '../../components/accounts/AccountPortfolioCard';
import AccountAdvancedSettingsCard from '../../components/accounts/AccountAdvancedSettingsCard';
import AccountTabs from '../../components/accounts/AccountTabs';
import DocumentListWidget from '../../components/documents/DocumentListWidget';
import { useConfirmation } from '../../components/providers/ConfirmationProvider';
import { getAccountTypeLabel } from '../../utils/accountLabels';
import { exportConsolidatedAccountLedgerPDF } from '../../utils/pdfGenerator';
import useAccountCodeWorkflow from '../../hooks/useAccountCodeWorkflow';

const formatDateInputValue = (date) => {
  const value = new Date(date);
  const offset = value.getTimezoneOffset();
  return new Date(value.getTime() - (offset * 60000)).toISOString().slice(0, 10);
};

const getYearStartDateInput = () => `${new Date().getFullYear()}-01-01`;

const normalizeAccountFormData = (account = {}, fallbackUserId = '') => ({
  name: account.name || '',
  type: account.type === 'OTHER' ? 'CUSTOMER' : account.type || 'CUSTOMER',
  // TEKLIF ana tip olduğu için geldiği gibi korunur; sadece legacy OTHER müşteri olarak normalize edilir.
  employmentType: account.employmentType || 'FIXED',
  personnelGroup: account.personnelGroup ?? null,
  dailyRate: account.dailyRate ?? 0,
  currency: account.currency || 'TRY',
  code: account.code || '',
  taxOffice: account.taxOffice || '',
  taxNumber: account.taxNumber || '',
  email: account.email || '',
  phone: account.phone || '',
  phone2: account.phone2 || '',
  address: account.address || '',
  country: account.country || '',
  city: account.city || '',
  district: account.district || '',
  transportMode: account.transportMode || 'None',
  serviceType: account.serviceType || 'None',
  assignedTo: account.assignedTo?._id || account.assignedTo || fallbackUserId || '',
  notes: account.notes || ''
});

const getPersonLabel = (person) => {
  if (!person || typeof person === 'string') return '';
  return [person?.name, person?.surname].filter(Boolean).join(' ') || person?.email || '';
};

const AccountDetail = () => {
  const { id } = useParams();
  const { user } = useAuthStore();
  const hasPermission = useAuthStore((state) => state.hasPermission);
  const navigate = useNavigate();
  const { confirm } = useConfirmation();
  const isNew = !id || id === 'new';
  const isInvalidAccountId = !isNew && (id === 'undefined' || id === 'null');
  const canWriteOperation = hasPermission('operation', 'write');

  const {
    fetchAccountById,
    saveAccount,
    getNextCode,
    checkCodeUsage,
    deleteAccount,
    fetchAccountLedger,
    fetchAccountOperations,
    clearCurrentAccount,
    currentLedgerReport,
    currentOperations,
    currentOperationsError
  } = useAccountStore();
  const { fetchAccountTransactions } = useTransactionStore();

  const [activeTab, setActiveTab] = useState('general');
  const [loading, setLoading] = useState(false);
  const [isTransModalOpen, setIsTransModalOpen] = useState(false);
  const [accountData, setAccountData] = useState(null);
  const [ledgerStartDate] = useState(() => getYearStartDateInput());
  const [ledgerEndDate] = useState(() => formatDateInputValue(new Date()));

  const [formData, setFormData] = useState(() => normalizeAccountFormData({}, user?._id));

  const headerName = formData.name || accountData?.name || 'Yeni Cari Kartı';
  const headerCode = formData.code || accountData?.code || '-';
  const headerType = getAccountTypeLabel(formData.type || accountData?.type || 'CUSTOMER');
  const representativeSource = accountData?.assignedTo || (
    user?._id && formData.assignedTo === user._id ? user : null
  );
  const representativeName = getPersonLabel(representativeSource);
  const ledgerReport = currentLedgerReport || {
    sections: [],
    summary: [],
    hasTransactions: false
  };

  const {
    codeChecking,
    isCodeAvailable
  } = useAccountCodeWorkflow({
    code: formData.code,
    setCode: (nextCode) => setFormData((prev) => ({ ...prev, code: nextCode })),
    type: formData.type,
    originalCode: accountData?.code,
    originalType: accountData?.type,
    accountId: id,
    isNew,
    enabled: isNew || Boolean(accountData),
    getNextCode,
    checkCodeUsage
  });

  // 1. Veri Yükleme (Init)
  useEffect(() => {
    const init = async () => {
      if (isInvalidAccountId) {
        toast.error("Geçersiz cari bağlantısı.");
        navigate('/accounts', { replace: true });
        return;
      }

      if (isNew) {
        clearCurrentAccount();
        setAccountData(null);
        setActiveTab('general');
        setFormData(normalizeAccountFormData({}, user?._id));
        return;
      }

      if (!isNew) {
        const data = await fetchAccountById(id);
        if (data) {
          setAccountData(data);
          setFormData(normalizeAccountFormData(data, user?._id));
          await fetchAccountTransactions(id);
        }
      }
    };
    init();
  }, [id, isInvalidAccountId, isNew, clearCurrentAccount, fetchAccountById, fetchAccountTransactions, navigate, user?._id]);

  useEffect(() => {
    if (isNew) return;

    if (activeTab === 'ledger') {
      fetchAccountLedger(id, {
        startDate: ledgerStartDate,
        endDate: ledgerEndDate
      });
    }

    if (activeTab === 'operations') {
      fetchAccountOperations(id);
    }
  }, [activeTab, fetchAccountLedger, fetchAccountOperations, id, isNew, ledgerStartDate, ledgerEndDate]);

  const handleUpdate = async (e) => {
    e.preventDefault();
    if (!isCodeAvailable) return toast.error("Lütfen benzersiz bir kod girin.");
    setLoading(true);
    const res = await saveAccount({
      ...formData,
      assignedTo: formData.assignedTo || user?._id || ''
    }, isNew ? null : id);
    if (res.success) {
      toast.success(isNew ? "Cari başarıyla oluşturuldu" : "Değişiklikler kaydedildi");
      if (res.data) {
        setAccountData(res.data);
        setFormData((prev) => ({ ...prev, ...normalizeAccountFormData(res.data, user?._id) }));
      }
      if (isNew) navigate('/accounts');
    }
    setLoading(false);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleDeleteAccount = async () => {
    if (isNew) return;
    if (await confirm(`${accountData?.name || 'Bu cari hesabı'} silmek istediğinize emin misiniz?`)) {
      const res = await deleteAccount(id);
      if (res.success) {
        toast.success("Cari hesap silindi");
        navigate('/accounts');
      } else {
        toast.error(res.message || "Cari hesap silinemedi", { duration: 6000 });
      }
    }
  };


  const refreshAccountSnapshot = async () => {
    if (isNew) return;

    const [freshAccount] = await Promise.all([
      fetchAccountById(id),
      fetchAccountTransactions(id),
      activeTab === 'ledger'
        ? fetchAccountLedger(id, {
            startDate: ledgerStartDate,
            endDate: ledgerEndDate
          })
        : Promise.resolve([])
    ]);

    if (freshAccount) {
      setAccountData(freshAccount);
      setFormData((prev) => ({ ...prev, ...normalizeAccountFormData(freshAccount, user?._id) }));
    }
  };

  const handleTransactionModalClose = async () => {
    setIsTransModalOpen(false);
    await refreshAccountSnapshot();
  };

  const handleExportConsolidatedLedger = async ({ account, ledger, lang, startDate, endDate }) => {
    if (!account || !ledger) return;
    await exportConsolidatedAccountLedgerPDF(account, ledger, lang || 'tr', startDate, endDate);
  };

  const openOperationDetail = (operationId) => {
    if (!operationId) return;
    window.open(`/operations/${operationId}`, '_blank', 'noopener,noreferrer');
  };

  const openOperationCreate = () => {
    if (!id || isNew) return;
    window.open(`/operations/new?accountId=${id}`, '_blank', 'noopener,noreferrer');
  };

  if (!isNew && !accountData) {
    return (
      <div className="flex flex-col items-center justify-center min-h-screen gap-4">
        <Loader2 className="animate-spin text-blue-600" size={40} />
        <p className="text-slate-500 font-bold tracking-tight">Cari bilgileri getiriliyor...</p>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-7xl space-y-6 p-4 sm:p-6">
      {/* Kompakt Üst Alan */}
      <div className="print-hidden rounded-[2rem] border border-slate-100 bg-white px-4 py-3 shadow-sm sm:px-5">
        <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
          <div className="min-w-0 space-y-3">
            <button
              onClick={() => navigate('/accounts')}
              className="group inline-flex w-fit items-center gap-2 text-xs font-black uppercase tracking-widest text-slate-500 transition-colors hover:text-slate-900"
            >
              <ArrowLeft size={16} className="transition-transform group-hover:-translate-x-1" />
              Cari Listesi
            </button>

            <div className="flex min-w-0 flex-col gap-2">
              <h1 className="truncate text-2xl font-black tracking-tight text-slate-900 sm:text-[2rem]">
                {headerName}
              </h1>
              <div className="flex flex-wrap items-center gap-x-3 gap-y-2 text-[10px] font-black uppercase tracking-[0.18em] text-slate-500">
                {representativeName ? (
                  <p className="flex min-w-0 items-center gap-2 text-slate-500 normal-case tracking-normal">
                    <span className="shrink-0 text-[10px] font-black uppercase tracking-[0.18em] text-slate-400">
                      Müşteri Temsilcisi
                    </span>
                    <span className="truncate text-slate-700">{representativeName}</span>
                  </p>
                ) : null}
                <span className="rounded-full border border-slate-200 bg-slate-50 px-3 py-1 text-slate-600">
                  Cari Kodu: {headerCode}
                </span>
                <span className="rounded-full border border-slate-200 bg-slate-50 px-3 py-1 text-slate-600">
                  {headerType}
                </span>
              </div>
            </div>
          </div>

          {!isNew && (
            <div className="flex w-full flex-col gap-2 sm:w-auto sm:flex-row sm:items-center">
              <button
                onClick={() => setIsTransModalOpen(true)}
                className="inline-flex flex-1 items-center justify-center gap-2 rounded-2xl bg-emerald-600 px-5 py-3 text-xs font-black uppercase tracking-widest text-white transition-all hover:bg-emerald-700 active:scale-[0.99] sm:flex-none"
              >
                <Plus size={18} />
                Yeni Hareket Ekle
              </button>
              <button
                onClick={handleDeleteAccount}
                className="inline-flex flex-1 items-center justify-center gap-2 rounded-2xl border border-rose-100 bg-rose-50 px-5 py-3 text-xs font-black uppercase tracking-widest text-rose-600 transition-colors hover:bg-rose-100 sm:flex-none"
              >
                <Trash2 size={18} />
                Cari Hesabı Sil
              </button>
            </div>
          )}
        </div>

        <div className="mt-3 border-t border-slate-100 pt-3">
          <AccountTabs activeTab={activeTab} setActiveTab={setActiveTab} isNew={isNew} compact />
        </div>
      </div>

      {/* 3. Sekme İçerikleri */}
      <main className="min-h-[400px]">
        {activeTab === 'general' && (
          <div className="animate-in fade-in slide-in-from-bottom-2 duration-300">
            {isNew ? (
              <div className="space-y-6">
                <AccountForm
                  formData={formData}
                  handleChange={handleChange}
                  handleUpdate={handleUpdate}
                  loading={loading}
                  isNew={isNew}
                  isCodeAvailable={isCodeAvailable}
                  codeChecking={codeChecking}
                  accountData={accountData}
                />

                <div className="bg-white rounded-3xl border border-slate-100 shadow-sm overflow-hidden">
                  <div className="p-5 border-b border-slate-50 bg-slate-50/30 flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <div className="p-2 bg-indigo-100 text-indigo-600 rounded-xl">
                        <Notebook size={18} />
                      </div>
                      <span className="font-black text-slate-800 text-[10px] uppercase tracking-widest">Özel Notlar</span>
                    </div>
                  </div>

                  <div className="p-6">
                    <label className="flex flex-col gap-2">
                      <span className="text-[10px] font-black uppercase tracking-widest text-slate-400 ml-1">
                        İç Notlar
                      </span>
                      <textarea
                        name="notes"
                        value={formData.notes || ''}
                        onChange={handleChange}
                        rows={10}
                        placeholder="Bu müşteri için iç notlar, operasyon ipuçları veya özel hatırlatmalar..."
                        className="w-full rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm font-medium text-slate-700 outline-none resize-none focus:border-blue-500 focus:ring-2 focus:ring-blue-500/10"
                      />
                    </label>
                  </div>
                </div>
              </div>
            ) : (
              <div className="grid grid-cols-1 xl:grid-cols-4 gap-6">
                <div className="xl:col-span-2">
                  <AccountForm
                    formData={formData}
                    handleChange={handleChange}
                    handleUpdate={handleUpdate}
                    loading={loading}
                    isNew={isNew}
                    isCodeAvailable={isCodeAvailable}
                    codeChecking={codeChecking}
                    accountData={accountData}
                    hideAdvancedSections={!isNew}
                  />
                </div>

                <div className="xl:col-span-2 space-y-6">
                  <AccountPortfolioCard
                    account={accountData}
                    currentUser={user}
                    onAccountUpdate={(updatedAccount) => {
                      setAccountData(updatedAccount);
                      setFormData((prev) => ({ ...prev, ...updatedAccount }));
                    }}
                  />
                  <AccountAdvancedSettingsCard
                    formData={formData}
                    handleChange={handleChange}
                  />
                </div>
              </div>
            )}
          </div>
        )}

        {activeTab === 'ledger' && (
          <div className="animate-in fade-in slide-in-from-bottom-2 duration-300">
            <AccountTransactions
              accountData={accountData}
              ledgerReport={ledgerReport}
              onExport={handleExportConsolidatedLedger}
            />
          </div>
        )}

        {activeTab === 'invoices' && (
          <div className="animate-in fade-in slide-in-from-bottom-2 duration-300">
            {!isNew && (
              <AccountInvoicesTab
                accountId={id}
                accountName={accountData?.name}
              />
            )}
          </div>
        )}

        {activeTab === 'operations' && (
          <div className="grid grid-cols-1 xl:grid-cols-3 gap-6 animate-in zoom-in-95 duration-300">
            <div className="xl:col-span-3 bg-white rounded-[2.5rem] border border-slate-100 p-6 shadow-sm">
              <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-5">
                <div>
                  <h2 className="text-lg font-black text-slate-800">Operasyonlar</h2>
                  <p className="text-xs font-bold text-slate-400">Bu cariye ait lojistik iş listesi</p>
                </div>
              {canWriteOperation && (
                <button
                  type="button"
                  onClick={openOperationCreate}
                  className="flex items-center justify-center gap-2 px-5 py-3 bg-slate-900 hover:bg-black text-white rounded-2xl text-xs font-black uppercase tracking-widest"
                >
                  <Plus size={16} /> Yeni Lojistik Operasyon Aç
                </button>
              )}
              </div>

              <div className="overflow-x-auto">
                <table className="w-full text-left">
                  <thead>
                    <tr className="border-b border-slate-100 text-[10px] font-black text-slate-400 uppercase tracking-widest">
                      <th className="py-4 px-3">Operasyon No</th>
                      <th className="py-4 px-3">Hizmet</th>
                      <th className="py-4 px-3">Durum</th>
                      <th className="py-4 px-3">Evrak</th>
                    <th className="py-4 px-3 text-right">İşlem</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-50">
                    {currentOperations.map((operation) => {
                      const statusClass = {
                        Pending: 'bg-amber-50 text-amber-700 border-amber-100',
                        In_Transit: 'bg-blue-50 text-blue-700 border-blue-100',
                        Customs: 'bg-purple-50 text-purple-700 border-purple-100',
                        Delivered: 'bg-emerald-50 text-emerald-700 border-emerald-100',
                        Cancelled: 'bg-rose-50 text-rose-700 border-rose-100'
                      }[operation.status] || 'bg-slate-50 text-slate-600 border-slate-100';

                      return (
                        <tr
                          key={operation._id}
                          className="cursor-pointer transition-colors hover:bg-slate-50"
                          onClick={() => openOperationDetail(operation._id)}
                        >
                          <td className="py-4 px-3">
                            <div className="font-black text-slate-800">{operation.projectNo}</div>
                            <div className="text-[10px] font-bold text-slate-400">{new Date(operation.createdAt).toLocaleDateString('tr-TR')}</div>
                          </td>
                          <td className="py-4 px-3">
                            <div className="font-bold text-sm text-slate-700">{operation.serviceType}</div>
                            <div className="text-[10px] font-bold text-slate-400">{operation.operationType} / {operation.transportMode}</div>
                          </td>
                          <td className="py-4 px-3">
                            <span className={`inline-flex px-3 py-1 rounded-lg text-[10px] font-black border ${statusClass}`}>
                              {operation.status}
                            </span>
                          </td>
                          <td className="py-4 px-3">
                            {operation.requiredDocumentsStatus === 'Missing' ? (
                              <span className="inline-flex items-center gap-1 text-rose-600 font-black text-xs">
                                <AlertTriangle size={15} /> Missing
                              </span>
                            ) : operation.requiredDocumentsStatus === 'Complete' ? (
                              <span className="inline-flex items-center gap-1 text-emerald-600 font-black text-xs">
                                <CheckCircle2 size={15} /> Complete
                              </span>
                            ) : (
                              <span className="text-slate-400 font-black text-xs">None</span>
                            )}
                          </td>
                          <td className="py-4 px-3 text-right">
                            <button
                              type="button"
                              onClick={(e) => {
                                e.stopPropagation();
                                openOperationDetail(operation._id);
                              }}
                              className="rounded-2xl bg-blue-600 px-4 py-2 text-xs font-black text-white transition-colors hover:bg-blue-700"
                            >
                              Detay
                            </button>
                          </td>
                        </tr>
                      );
                    })}
                    {currentOperations.length === 0 && (
                      <tr>
                        <td colSpan="5" className="py-16 text-center text-slate-400 font-bold">
                          {currentOperationsError || 'Bu cari için henüz lojistik proje açılmadı.'}
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>

          </div>
        )}

        {activeTab === 'notes' && (
          <div className="bg-white p-6 rounded-[2.5rem] border border-slate-100 animate-in fade-in duration-300">
            <label className="text-[10px] font-black text-slate-400 uppercase ml-4 mb-2 block">Cari Notları</label>
            <textarea 
              className="w-full h-80 p-6 bg-slate-50 rounded-3xl border-none focus:ring-2 ring-blue-500/10 font-medium text-slate-600 outline-none resize-none"
              placeholder="Müşteri talepleri, özel anlaşmalar veya takip notları..."
              value={formData.notes}
              name="notes"
              onChange={handleChange}
            />
          </div>
        )}

        {activeTab === 'documents' && (
          <div className="animate-in fade-in slide-in-from-bottom-2 duration-300">
            {!isNew && (
              <DocumentListWidget category="ACCOUNT" parentResourceId={id} title="Müşteri Evrakları" />
            )}
          </div>
        )}
      </main>

      {isTransModalOpen && (
      <TransactionModal 
          isOpen={isTransModalOpen} 
          onClose={handleTransactionModalClose} 
          accountId={id} 
          key={isTransModalOpen ? `modal-${id}` : 'closed'}
        />
      )}
    </div>
  );
};

export default AccountDetail;
